/**
 * StudentDataContext - The single source of truth for student data
 *
 * Design principles:
 * 1. URL is the source of truth - grade, term, week, lesson all come from URL
 * 2. No caching - every page load fetches fresh data
 * 3. No Redux - just React state for what's needed
 * 4. Simple - fetch what the current page needs, nothing more
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode
} from "react";
import {
  useParams,
  useNavigate,
  UNSAFE_NavigationContext as UnsafeNavigationContext
} from "react-router-dom";

/**
 * Safe version of useNavigate that returns a no-op if outside Router context.
 * This allows tests to run without wrapping in MemoryRouter.
 */
function useSafeNavigate() {
  const navContext = useContext(UnsafeNavigationContext);
  // If no router context (null or no navigator), return a no-op function
  if (!navContext || !navContext.navigator) {
    return () => {
      // No-op when outside router
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useNavigate();
}

import type {
  Term,
  Week,
  Subject,
  Lesson,
  CsatTriggerPayload
} from "@ts/interfaces/terms/api/termsApiInterface";
import type { Grade } from "@ts/interfaces/student/studentAccountInterface";
import type {
  DashboardDetailsData
} from "@ts/interfaces/student/api/studentAccountApiInterface";

import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { GRADE_STATUS } from "@ts/enums/GradeStatus";
import { GradeTheme } from "@euka-mfes/bark";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";
import { mapGradeParamIdToGradeCode, stripPrefix } from "@utils/routeUtil";
import {
  useSubjectsData,
  useMaterialsData,
  useAchievementsData,
  useAssignmentsData,
  useTermsData,
  useWeekLessonsData,
  useUserData,
  type SubjectsData,
  type MaterialsData,
  type AchievementsData,
  type AssignmentsData,
  type HeaderDetails
} from "./data";

// ============================================
// Types
// ============================================

interface StudentDataContextValue {
  // Header/User data
  headerDetails: HeaderDetails;
  headerDetailsStatus: REQUEST_STATUS;
  studentsFamilyAvatars: number[];
  dashboardDetails: DashboardDetailsData | null;

  // Grade (derived from URL + headerDetails)
  selectedGrade: Grade | null;
  gradeTheme: GradeTheme;

  // Grade activation flow
  activatingGradeCode: number | null;
  setActivatingGradeCode: (code: number | null) => void;
  changedGrade: Grade | null;
  setChangedGrade: (grade: Grade | null) => void;

  // Terms data
  terms: Term[];
  termsStatus: REQUEST_STATUS;
  termsError: ErrorInterface | null;
  termsForGradeCode: number | undefined;
  selectedTerm: Term | null;
  selectedWeek: Week | null;
  setSelectedTerm: (term: Term | null) => void;
  setSelectedWeek: (week: Week | null) => void;

  // Week lessons data
  currentWeekSubjects: Subject[];
  weekLessonsStatus: REQUEST_STATUS;
  weekLessonsError: ErrorInterface | null;
  selectedLesson: Lesson | null;
  selectedSubject: Subject | null;

  // Lesson UI state
  showBlockNavigationModal: boolean;
  setShowBlockNavigationModal: (show: boolean) => void;
  pendingNavigationPath: string | null;
  setPendingNavigationPath: (path: string | null) => void;
  hasAttemptedApplyQuestion: boolean;
  setHasAttemptedApplyQuestion: (attempted: boolean) => void;
  isApplyResultViewShown: boolean | null;
  setApplyResultViewShown: (shown: boolean | null) => void;
  isReviewContentSidebarOpen: boolean;
  openReviewContentSidebar: () => void;
  closeReviewContentSidebar: () => void;

  // Actions - Lessons
  fetchSectionContent: <T>(params: {
    section: string;
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<T>;
  startLesson: (params: {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<void>;
  completeLesson: (params: {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<CsatTriggerPayload | null>;

  // Actions - User
  fetchHeaderDetails: () => Promise<void>;
  ensureFamilyAvatars: () => void;
  fetchFamilyAvatars: () => void;
  updateAvatar: (avatarKey: number) => Promise<{ success: boolean }>;
  changePassword: (data: { current_password: string; new_password: string; new_password_confirmation: string }) => Promise<{ success: boolean }>;
  completeOrientation: (gradeCode: number) => Promise<{ success: boolean }>;
  updateUserState: (data: { intro_screen_seen?: boolean }) => Promise<{ success: boolean }>;

  // Actions - Grade change
  fetchGradeChangeOtp: (gradeCode: number) => Promise<void>;
  validateGradeChangeOtp: (otp: string) => Promise<void>;

  // Request statuses for UI
  getDashboardDetailsRequestStatus: REQUEST_STATUS;
  getGradeChangeOtpRequestStatus: REQUEST_STATUS;
  validateGradeChangeOtpRequestStatus: REQUEST_STATUS;
  updateAvatarRequestStatus: REQUEST_STATUS;
  completeOrientationRequestStatus: REQUEST_STATUS;
  changePasswordRequestStatus: REQUEST_STATUS;
  changePasswordRequestError: ErrorInterface | null;
  validateGradeChangeOtpRequestError: ErrorInterface | null;

  // Reset functions
  resetTermsDetails: () => void;
  invalidateAllCachedData: () => void;
  resetLessonState: () => void;
  resetAvatarState: () => void;
  resetChangePasswordState: () => void;

  // Loading helper
  isDataReady: boolean;

  // Composed data domains (from separate hooks for modularity)
  subjectsData: SubjectsData;
  materialsData: MaterialsData;
  achievementsData: AchievementsData;
  assignmentsData: AssignmentsData;
}

// ============================================
// Context
// ============================================

const StudentDataContext = createContext<StudentDataContextValue | null>(null);

// ============================================
// Provider
// ============================================

export function StudentDataProvider({ children }: { children: ReactNode }) {
  const navigate = useSafeNavigate();

  // ─────────────────────────────────────────────────────────────
  // URL params - THE source of truth
  // ─────────────────────────────────────────────────────────────
  const {
    gradeId,
    termId,
    weekId,
    lessonSlug,
    lessonId,
    subjectSlug,
    assignmentId
  } = useParams<{
    gradeId?: string;
    termId?: string;
    weekId?: string;
    lessonSlug?: string;
    lessonId?: string;
    subjectSlug?: string;
    assignmentId?: string;
  }>();

  const urlGradeCode = gradeId ? mapGradeParamIdToGradeCode(gradeId) : undefined;
  const urlTermId = termId ? stripPrefix(termId, "term-") : undefined;
  const urlWeekId = weekId ? stripPrefix(weekId, "week-") : undefined;

  // Both assignments (term/:termId) and terms (term-N stripped) routes
  // resolve termId to a raw number. urlTermId holds the stripped value.
  const urlTermNumber = urlTermId ? Number(urlTermId) : undefined;

  // ─────────────────────────────────────────────────────────────
  // User data (from useUserData hook) — must come before composed
  // data domains so headerDetails / selectedGrade are available
  // for the assignments feature-flag gate.
  // ─────────────────────────────────────────────────────────────
  const userData = useUserData({ urlGradeCode });

  const {
    headerDetails,
    headerDetailsStatus,
    studentsFamilyAvatars,
    dashboardDetails,
    selectedGrade,
    gradeTheme,
    activatingGradeCode,
    setActivatingGradeCode,
    changedGrade,
    setChangedGrade,
    fetchHeaderDetails,
    ensureFamilyAvatars,
    fetchFamilyAvatars,
    updateAvatar,
    changePassword,
    completeOrientation,
    updateUserState,
    fetchGradeChangeOtp,
    validateGradeChangeOtp,
    getDashboardDetailsRequestStatus,
    getGradeChangeOtpRequestStatus,
    validateGradeChangeOtpRequestStatus,
    updateAvatarRequestStatus,
    completeOrientationRequestStatus,
    changePasswordRequestStatus,
    changePasswordRequestError,
    validateGradeChangeOtpRequestError,
    resetAvatarState,
    resetChangePasswordState
  } = userData;

  // ─────────────────────────────────────────────────────────────
  // Composed data domains (from separate hooks for modularity)
  // ─────────────────────────────────────────────────────────────
  const subjectsData = useSubjectsData({
    urlGradeCode,
    urlSubjectSlug: subjectSlug
  });
  const materialsData = useMaterialsData({ urlGradeCode });
  const achievementsData = useAchievementsData({ urlGradeCode });

  // Gate assignments fetching on the per-grade feature flag alone.
  // Non-assessed students can also access assignments (they see an
  // "assessment-free" variant of the UI with no marks/badges).
  // Before selectedGrade resolves, this defaults to false so the gate
  // naturally blocks premature fetches.
  const assignmentsEnabled =
    selectedGrade?.assignmentsInLearningEnabled ?? false;
  const assignmentsData = useAssignmentsData({
    urlGradeCode,
    urlTermNumber,
    urlAssignmentId: assignmentId,
    enabled: assignmentsEnabled
  });

  // ─────────────────────────────────────────────────────────────
  // Access control - redirect if grade invalid
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (headerDetailsStatus !== REQUEST_STATUS.SUCCESS) return;
    if (!urlGradeCode) return; // No grade in URL, nothing to validate

    const gradeFromUrl = headerDetails.grades.find(g => g.grade_code === urlGradeCode);

    // Grade not found
    if (!gradeFromUrl) {
      navigate("/learning/no-access", { replace: true });
      return;
    }

    // Grade not started (and not being activated)
    if (gradeFromUrl.status === GRADE_STATUS.NOT_STARTED && activatingGradeCode !== urlGradeCode) {
      // Allow orientation routes
      if (!window.location.pathname.includes("/orientation")) {
        navigate("/learning/no-access", { replace: true });
      }
    }
  }, [headerDetailsStatus, headerDetails.grades, urlGradeCode, activatingGradeCode, navigate]);

  // ─────────────────────────────────────────────────────────────
  // Terms data (from useTermsData hook)
  // ─────────────────────────────────────────────────────────────
  const termsData = useTermsData({
    urlGradeCode,
    urlTermId,
    urlWeekId,
    lessonId
  });

  const {
    terms,
    termsStatus,
    termsError,
    selectedTerm,
    selectedWeek,
    setSelectedTerm,
    setSelectedWeek,
    invalidateTermsCache,
    forGradeCode
  } = termsData;

  // ─────────────────────────────────────────────────────────────
  // Week lessons data (from useWeekLessonsData hook)
  // ─────────────────────────────────────────────────────────────
  const weekLessonsData = useWeekLessonsData({
    urlGradeCode,
    urlTermId,
    urlWeekId,
    lessonSlug,
    lessonId,
    invalidateTermsCache,
    invalidateSubjectsCache: subjectsData.invalidateSubjectsCache
  });

  const {
    currentWeekSubjects,
    weekLessonsStatus,
    weekLessonsError,
    selectedSubject,
    selectedLesson,
    startLesson,
    completeLesson,
    fetchSectionContent,
    invalidateLessonsCache
  } = weekLessonsData;

  // ─────────────────────────────────────────────────────────────
  // Lesson UI state
  // ─────────────────────────────────────────────────────────────
  const [showBlockNavigationModal, setShowBlockNavigationModal] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const [hasAttemptedApplyQuestion, setHasAttemptedApplyQuestion] = useState(false);
  const [isApplyResultViewShown, setApplyResultViewShown] = useState<boolean | null>(null);
  const [isReviewContentSidebarOpen, setIsReviewContentSidebarOpen] = useState(false);

  const openReviewContentSidebar = useCallback(() => setIsReviewContentSidebarOpen(true), []);
  const closeReviewContentSidebar = useCallback(() => setIsReviewContentSidebarOpen(false), []);

  // ─────────────────────────────────────────────────────────────
  // Reset functions
  // ─────────────────────────────────────────────────────────────
  // resetTermsDetails invalidates the cache so data will be refetched
  const resetTermsDetails = invalidateTermsCache;

  // Invalidate all grade-level cached data (terms, subjects, week lessons).
  // Used by cross-tab sync to ensure all views refresh when a lesson is
  // started or completed in another browser tab. See: BLD2-547.
  const invalidateAllCachedData = useCallback(() => {
    invalidateTermsCache();
    // invalidateLessonsCache internally calls invalidateSubjectsCache,
    // so this single call covers both week lessons and subjects.
    invalidateLessonsCache();
  }, [invalidateTermsCache, invalidateLessonsCache]);

  const resetLessonState = useCallback(() => {
    // selectedLesson and selectedSubject are derived from URL, no state to reset
    setShowBlockNavigationModal(false);
    setPendingNavigationPath(null);
    setHasAttemptedApplyQuestion(false);
    setApplyResultViewShown(null);
    setIsReviewContentSidebarOpen(false);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Loading state helper
  // ─────────────────────────────────────────────────────────────
  const isDataReady = useMemo(() => {
    if (lessonId && lessonSlug) {
      return !!(selectedTerm && selectedWeek && selectedLesson && selectedSubject);
    }
    if (urlWeekId) {
      return !!(selectedTerm && selectedWeek);
    }
    if (urlTermId) {
      return !!selectedTerm;
    }
    return true;
  }, [lessonId, lessonSlug, urlWeekId, urlTermId, selectedTerm, selectedWeek, selectedLesson, selectedSubject]);

  // ─────────────────────────────────────────────────────────────
  // Context value
  // ─────────────────────────────────────────────────────────────
  const value = useMemo<StudentDataContextValue>(
    () => ({
      // Header/User
      headerDetails,
      headerDetailsStatus,
      studentsFamilyAvatars,
      dashboardDetails,

      // Grade
      selectedGrade,
      gradeTheme,
      activatingGradeCode,
      setActivatingGradeCode,
      changedGrade,
      setChangedGrade,

      // Terms
      terms,
      termsStatus,
      termsError,
      termsForGradeCode: forGradeCode,
      selectedTerm,
      selectedWeek,
      setSelectedTerm,
      setSelectedWeek,

      // Week lessons
      currentWeekSubjects,
      weekLessonsStatus,
      weekLessonsError,
      selectedLesson,
      selectedSubject,

      // Lesson UI
      showBlockNavigationModal,
      setShowBlockNavigationModal,
      pendingNavigationPath,
      setPendingNavigationPath,
      hasAttemptedApplyQuestion,
      setHasAttemptedApplyQuestion,
      isApplyResultViewShown,
      setApplyResultViewShown,
      isReviewContentSidebarOpen,
      openReviewContentSidebar,
      closeReviewContentSidebar,

      // Lesson actions
      fetchSectionContent,
      startLesson,
      completeLesson,

      // User actions
      fetchHeaderDetails,
      ensureFamilyAvatars,
      fetchFamilyAvatars,
      updateAvatar,
      changePassword,
      completeOrientation,
      updateUserState,

      // Grade change actions
      fetchGradeChangeOtp,
      validateGradeChangeOtp,

      // Request statuses
      getDashboardDetailsRequestStatus,
      getGradeChangeOtpRequestStatus,
      validateGradeChangeOtpRequestStatus,
      updateAvatarRequestStatus,
      completeOrientationRequestStatus,
      changePasswordRequestStatus,
      changePasswordRequestError,
      validateGradeChangeOtpRequestError,

      // Reset
      resetTermsDetails,
      invalidateAllCachedData,
      resetLessonState,
      resetAvatarState,
      resetChangePasswordState,

      // Loading
      isDataReady,

      // Composed data domains
      subjectsData,
      materialsData,
      achievementsData,
      assignmentsData
    }),
    [
      // User data - only include values that change and should trigger re-renders
      headerDetailsStatus,
      gradeTheme,
      selectedGrade,
      // Terms data
      terms,
      termsStatus,
      termsError,
      forGradeCode,
      selectedTerm,
      selectedWeek,
      setSelectedTerm,
      setSelectedWeek,
      // Week lessons data
      currentWeekSubjects,
      weekLessonsStatus,
      weekLessonsError,
      selectedLesson,
      selectedSubject,
      fetchSectionContent,
      startLesson,
      completeLesson,
      // Lesson UI state
      showBlockNavigationModal,
      pendingNavigationPath,
      hasAttemptedApplyQuestion,
      isApplyResultViewShown,
      isReviewContentSidebarOpen,
      openReviewContentSidebar,
      closeReviewContentSidebar,
      // Reset functions
      resetTermsDetails,
      invalidateAllCachedData,
      resetLessonState,
      // Loading helper
      isDataReady,
      // Composed data domains
      subjectsData,
      materialsData,
      achievementsData,
      assignmentsData
    ]
  );

  return (
    <StudentDataContext.Provider value={value}>
      {children}
    </StudentDataContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useStudentData() {
  const context = useContext(StudentDataContext);
  if (!context) {
    throw new Error("useStudentData must be used within a StudentDataProvider");
  }
  return context;
}
